import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Input,
  Radio,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import Select from "@/components/Common/AppSelect"
import type { ProductListItemDTO } from "../../../api/productList"
import {
  ProductionOrderService,
  type TechnologyResponseDTO,
  TechnologyService,
} from "../../../client"
import { getLicenseErrorDetails } from "../../../utils/licenseErrors"
import UnifiedProductPickerModal from "../Modals/UnifiedProductPickerModal"
import { RowProductSelector } from "../shared/SingleWarehouseFormSections"
import {
  findTechnologyInputProductConflicts,
  findTechnologyInputProductUsagesOutsideTarget,
  formatTechnologyInputProductUsage,
} from "./technologyInputProductConflicts"

type Props = {
  id: string
}

/** Normalizes technology codes to the required uppercase display format. */
const normalizeTechnologyCode = (value: string) =>
  value.trim().toLocaleUpperCase("pl-PL")

export default function TechnologyPage({ id }: Props) {
  const toast = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const rowHover = useColorModeValue("gray.50", "whiteAlpha.100")
  const sectionBackground = useColorModeValue("gray.50", "whiteAlpha.50")
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  const { data, isPending, isError, error } = useQuery<TechnologyResponseDTO>({
    queryKey: ["technologies", "detail", id],
    queryFn: () => TechnologyService.getTechnologyByID(id),
  })
  const { data: productionOrderUsage } = useQuery({
    queryKey: ["production-orders", "usage-by-technology", id],
    queryFn: () =>
      ProductionOrderService.getAllProductionOrders(
        undefined,
        undefined,
        id,
        undefined,
        1,
        1,
      ),
  })
  const isUsedInProductionOrders = (productionOrderUsage?.total ?? 0) > 0

  const { mutate: deleteTech, isPending: isDeleting } = useMutation({
    mutationKey: ["technologies", "delete", id],
    mutationFn: () => TechnologyService.deleteTechnologyByID(id),
    onSuccess: async (result) => {
      toast({
        title:
          result?.status === "archived"
            ? "Technologię zarchiwizowano"
            : "Technologię usunięto",
        status: "success",
      })
      await qc.invalidateQueries({ queryKey: ["technologies", "list"] })
      navigate({
        to: "/WMS/technologies/technologies-list",
        search: { page: 1, page_size: 25, status: "active" },
      })
    },
    onError: (err: any) => {
      const licenseError = getLicenseErrorDetails(err)
      toast({
        title: licenseError?.title ?? "Błąd usuwania",
        description:
          licenseError?.description ??
          err?.body?.error ??
          err?.message ??
          "Spróbuj ponownie.",
        status: "error",
        duration: 7000,
      })
    },
  })

  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    version: "1",
    is_active: true,
    requires_expiration_date: false,
    validity_days: "",
  })
  const [inputs, setInputs] = useState<
    Array<{
      product_id: string
      qty: string
      loss_percent: string
      cost_mode: string
      service_cost_netto: string
      service_cost_source: string
      cost_note: string
      substitutes: Array<{
        product_id: string
        qty: string
        loss_percent: string
        service_cost_netto: string
        priority: string
        note: string
      }>
    }>
  >([])
  const [outputs, setOutputs] = useState<
    Array<{
      product_id: string
      qty: string
      is_main: boolean
    }>
  >([])
  const highlightedProductIds = useMemo(
    () =>
      Array.from(
        new Set(
          [...inputs, ...inputs.flatMap((item) => item.substitutes), ...outputs]
            .map((item) => item.product_id)
            .filter(Boolean),
        ),
      ),
    [inputs, outputs],
  )
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerTarget, setPickerTarget] = useState<{
    type: "input" | "output" | "substitute"
    index: number
    substituteIndex?: number
  } | null>(null)
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, ProductListItemDTO>
  >({})
  const inputProductRows = useMemo(
    () =>
      inputs.map((row) => ({
        productId: row.product_id,
        productLabel: selectedProducts[row.product_id]
          ? `${selectedProducts[row.product_id].sku} — ${selectedProducts[row.product_id].name}`
          : undefined,
        substitutes: row.substitutes.map((substitute) => ({
          productId: substitute.product_id,
          productLabel: selectedProducts[substitute.product_id]
            ? `${selectedProducts[substitute.product_id].sku} — ${selectedProducts[substitute.product_id].name}`
            : undefined,
        })),
      })),
    [inputs, selectedProducts],
  )
  const inputProductConflicts = useMemo(
    () => findTechnologyInputProductConflicts(inputProductRows),
    [inputProductRows],
  )

  const resetForm = useCallback((tech: TechnologyResponseDTO) => {
    const seededProducts: Record<string, ProductListItemDTO> = {}
    for (const row of [
      ...(tech.inputs ?? []),
      ...(tech.inputs ?? []).flatMap((input) => input.substitutes ?? []),
      ...(tech.outputs ?? []),
    ]) {
      const product = (row as any).product as any
      if (!product?.id) continue
      seededProducts[product.id] = {
        id: product.id,
        manufacturer: product.manufacturer ?? "",
        sku: product.sku ?? "",
        name: product.name ?? "",
        EAN: product.EAN ?? product.ean ?? "",
        unit: product.unit ?? "",
        is_active: product.is_active ?? true,
        is_service: product.is_service ?? false,
        acronym: product.acronym ?? "",
        created_at: product.created_at ?? "",
      }
    }
    setSelectedProducts(seededProducts)
    setForm({
      code: normalizeTechnologyCode(tech.code ?? ""),
      name: tech.name ?? "",
      description: tech.description ?? "",
      version: `${tech.version ?? 1}`,
      is_active: !!tech.is_active,
      requires_expiration_date: !!tech.requires_expiration_date,
      validity_days: tech.validity_days ? `${tech.validity_days}` : "",
    })
    setInputs(
      (tech.inputs ?? []).map((row) => ({
        product_id: row.product_id,
        qty: `${row.qty ?? ""}`,
        loss_percent: `${row.loss_percent ?? ""}`,
        cost_mode: row.cost_mode ?? "",
        service_cost_netto: `${row.service_cost_netto ?? ""}`,
        service_cost_source: row.service_cost_source ?? "",
        cost_note: row.cost_note ?? "",
        substitutes: (row.substitutes ?? []).map((sub) => ({
          product_id: sub.substitute_product_id,
          qty: `${sub.qty ?? ""}`,
          loss_percent: `${sub.loss_percent ?? ""}`,
          service_cost_netto: `${sub.service_cost_netto ?? ""}`,
          priority: `${sub.priority ?? "1"}`,
          note: sub.note ?? "",
        })),
      })),
    )
    setOutputs(
      (tech.outputs ?? []).map((row) => ({
        product_id: row.product_id,
        qty: `${row.qty ?? ""}`,
        is_main: !!row.is_main,
      })),
    )
  }, [])

  useEffect(() => {
    if (data) {
      resetForm(data)
    }
  }, [data, resetForm])

  const inputsValid = useMemo(() => {
    return (
      inputs.length > 0 &&
      inputs.every((row) => {
        const qty = Number.parseFloat(row.qty)
        if (!(row.product_id && Number.isFinite(qty) && qty > 0)) return false
        const isServiceInput =
          selectedProducts[row.product_id]?.is_service ||
          row.cost_mode === "fixed_service_rate"
        if (isServiceInput) {
          const serviceCost = Number.parseFloat(row.service_cost_netto)
          if (!(Number.isFinite(serviceCost) && serviceCost > 0)) return false
        }
        return row.substitutes.every((sub) => {
          const qty = Number.parseFloat(sub.qty)
          if (!(sub.product_id && Number.isFinite(qty) && qty > 0)) return false
          if (selectedProducts[sub.product_id]?.is_service) {
            const serviceCost = Number.parseFloat(sub.service_cost_netto)
            return Number.isFinite(serviceCost) && serviceCost > 0
          }
          return true
        })
      })
    )
  }, [inputs, selectedProducts])

  const outputsValid = useMemo(() => {
    return (
      outputs.length > 0 &&
      outputs.filter((row) => row.is_main).length === 1 &&
      outputs.every((row) => {
        const qty = Number.parseFloat(row.qty)
        return row.product_id && Number.isFinite(qty) && qty > 0
      })
    )
  }, [outputs])

  const mainOutputProduct = outputs.find((row) => row.is_main)?.product_id
    ? selectedProducts[outputs.find((row) => row.is_main)!.product_id]
    : undefined

  const mainProductRequiresExpiration =
    mainOutputProduct?.requires_expiration_date === true
  const isExpirationValid =
    !mainProductRequiresExpiration || form.requires_expiration_date

  const canSave =
    form.code.trim() !== "" &&
    form.name.trim() !== "" &&
    inputsValid &&
    inputProductConflicts.length === 0 &&
    outputsValid &&
    isExpirationValid

  const openPicker = (
    type: "input" | "output" | "substitute",
    index: number,
    substituteIndex?: number,
  ) => {
    setPickerTarget({ type, index, substituteIndex })
    setPickerOpen(true)
  }

  const handleHeaderActionChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const action = event.target.value
    if (!data) return

    switch (action) {
      case "variant":
        navigate({
          to: "/WMS/technologies/create",
          search: { clone_from: data.id, mode: "variant" },
        })
        return
      case "version":
        navigate({
          to: "/WMS/technologies/create",
          search: { clone_from: data.id, mode: "version" },
        })
        return
      case "edit":
        if (!isUsedInProductionOrders) {
          setEditMode(true)
        }
        return
      case "delete":
        if (!data?.is_active) {
          onOpen()
        }
        return
      default:
        return
    }
  }

  const handleChooseProduct = (p: ProductListItemDTO) => {
    if (!pickerTarget) return
    if (pickerTarget.type === "input" || pickerTarget.type === "substitute") {
      const target =
        pickerTarget.type === "substitute"
          ? {
              role: "substitute" as const,
              inputIndex: pickerTarget.index,
              substituteIndex: pickerTarget.substituteIndex,
            }
          : {
              role: "primary" as const,
              inputIndex: pickerTarget.index,
            }
      const existingUsages = findTechnologyInputProductUsagesOutsideTarget(
        inputProductRows,
        p.id,
        target,
      )
      if (existingUsages.length > 0) {
        const productLabel = `${p.sku} — ${p.name}`
        toast({
          title: "Ten materiał jest już użyty",
          description: `Produkt „${productLabel}” jest już użyty ${existingUsages.map(formatTechnologyInputProductUsage).join(" oraz ")}. Każdy materiał może wystąpić tylko raz w wejściach technologii — jako materiał podstawowy albo jako zamiennik. Wybierz inny produkt.`,
          status: "error",
          duration: 7000,
        })
        return
      }
    }
    setSelectedProducts((prev) => ({ ...prev, [p.id]: p }))
    if (pickerTarget.type === "input") {
      setInputs((prev) => {
        const next = [...prev]
        next[pickerTarget.index] = {
          ...next[pickerTarget.index],
          product_id: p.id,
        }
        return next
      })
    } else if (
      pickerTarget.type === "substitute" &&
      pickerTarget.substituteIndex != null
    ) {
      setInputs((prev) => {
        const next = [...prev]
        const substitutes = [...next[pickerTarget.index].substitutes]
        substitutes[pickerTarget.substituteIndex!] = {
          ...substitutes[pickerTarget.substituteIndex!],
          product_id: p.id,
        }
        next[pickerTarget.index] = {
          ...next[pickerTarget.index],
          substitutes,
        }
        return next
      })
    } else {
      setOutputs((prev) => {
        const next = [...prev]
        next[pickerTarget.index] = {
          ...next[pickerTarget.index],
          product_id: p.id,
        }
        return next
      })
    }
    setPickerOpen(false)
    setPickerTarget(null)
  }

  const { mutate: updateTech, isPending: isUpdating } = useMutation({
    mutationKey: ["technologies", "update", id],
    mutationFn: (payload: any) =>
      TechnologyService.updateTechnologyByID(id, payload),
    onSuccess: async () => {
      toast({ title: "Zapisano", status: "success", duration: 2000 })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["technologies", "detail", id] }),
        qc.invalidateQueries({ queryKey: ["technologies", "list"] }),
      ])
      setEditMode(false)
    },
    onError: (err: any) => {
      if (
        typeof err?.body?.error === "string" &&
        err.body.error.includes("Ten sam materiał został użyty więcej niż raz")
      ) {
        toast({
          title: "Materiał został użyty więcej niż raz",
          description: err.body.error,
          status: "error",
          duration: 7000,
        })
        return
      }
      const licenseError = getLicenseErrorDetails(err)
      toast({
        title: licenseError?.title ?? "Błąd zapisu",
        description:
          licenseError?.description ??
          err?.body?.error ??
          err?.message ??
          "Spróbuj ponownie",
        status: "error",
        duration: 7000,
      })
    },
  })

  if (isPending) {
    return (
      <Flex justify="center" align="center" w="full" h="60vh">
        <Spinner size="xl" color="main.accent" />
      </Flex>
    )
  }

  if (isError || !data) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Nie udało się pobrać technologii: {(error as any)?.message ?? "Błąd"}
      </Alert>
    )
  }

  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "—")
  const fmtMoney = (value?: number | null) =>
    typeof value === "number" && Number.isFinite(value)
      ? new Intl.NumberFormat("pl-PL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)
      : "—"

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={4}>
        <Box>
          <Heading size="lg">
            {editMode ? "Edycja technologii" : data.name}
          </Heading>
          {!editMode && (
            <Text color="gray.500">
              Kod: {normalizeTechnologyCode(data.code ?? "")}
            </Text>
          )}
        </Box>
        <HStack>
          {!editMode && (
            <Badge
              colorScheme={data.is_active ? "green" : "gray"}
              fontSize="0.9em"
            >
              {data.is_active ? "Aktywna" : "Nieaktywna"}
            </Badge>
          )}
          {editMode ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  resetForm(data)
                  setEditMode(false)
                }}
              >
                Anuluj
              </Button>
              <Button
                colorScheme="green"
                isLoading={isUpdating}
                isDisabled={!canSave}
                onClick={() => {
                  if (!canSave) return
                  updateTech({
                    code: normalizeTechnologyCode(form.code),
                    name: form.name.trim(),
                    description: form.description.trim() || undefined,
                    is_active: form.is_active,
                    requires_expiration_date: form.requires_expiration_date,
                    validity_days:
                      form.validity_days.trim() !== ""
                        ? Number.parseInt(form.validity_days, 10)
                        : undefined,
                    inputs: inputs.map((row) => {
                      const isServiceInput =
                        selectedProducts[row.product_id]?.is_service ||
                        row.cost_mode === "fixed_service_rate"

                      return {
                        product_id: row.product_id,
                        qty: Number.parseFloat(row.qty),
                        loss_percent:
                          row.loss_percent.trim() === ""
                            ? undefined
                            : Number.parseFloat(row.loss_percent),
                        cost_mode: isServiceInput
                          ? "fixed_service_rate"
                          : "purchase_history",
                        service_cost_netto: isServiceInput
                          ? Number.parseFloat(row.service_cost_netto)
                          : undefined,
                        service_cost_source: isServiceInput
                          ? row.service_cost_source || "technology_fixed_rate"
                          : undefined,
                        cost_note: row.cost_note.trim() || undefined,
                        substitutes: row.substitutes.map((sub, subIndex) => {
                          const isServiceSubstitute =
                            selectedProducts[sub.product_id]?.is_service
                          return {
                            substitute_product_id: sub.product_id,
                            qty: Number.parseFloat(sub.qty),
                            loss_percent:
                              sub.loss_percent.trim() === ""
                                ? undefined
                                : Number.parseFloat(sub.loss_percent),
                            cost_mode: isServiceSubstitute
                              ? "fixed_service_rate"
                              : "purchase_history",
                            service_cost_netto: isServiceSubstitute
                              ? Number.parseFloat(sub.service_cost_netto)
                              : undefined,
                            service_cost_source: isServiceSubstitute
                              ? "technology_fixed_rate"
                              : undefined,
                            priority:
                              Number.parseInt(sub.priority, 10) || subIndex + 1,
                            note: sub.note.trim() || undefined,
                          }
                        }),
                      }
                    }),
                    outputs: outputs.map((row) => ({
                      product_id: row.product_id,
                      qty: Number.parseFloat(row.qty),
                      is_main: row.is_main,
                    })),
                  })
                }}
              >
                Zapisz
              </Button>
            </>
          ) : (
            <Select
              size="sm"
              value=""
              onChange={handleHeaderActionChange}
              placeholder="Akcje"
              minW="160px"
            >
              <option value="" disabled>
                Akcje
              </option>
              <option value="variant">Utwórz wariant</option>
              <option value="version">Nowa wersja</option>
              <option value="edit" disabled={isUsedInProductionOrders}>
                Edytuj
              </option>
              <option value="delete" disabled={!!data?.is_active}>
                {isUsedInProductionOrders ? "Archiwizuj" : "Usuń"}
              </option>
            </Select>
          )}
        </HStack>
      </HStack>

      {!editMode && data?.is_active && (
        <Text mb={4} color="orange.500" fontSize="sm">
          Przed usunięciem lub archiwizacją ustaw technologię jako nieaktywną i
          zapisz zmiany.
        </Text>
      )}

      {!editMode && isUsedInProductionOrders && (
        <Alert status="info" borderRadius="md" mb={4}>
          <AlertIcon />
          Ta technologia była już użyta w zleceniach produkcyjnych. Zmienioną
          recepturę zapisz jako `Nowa wersja`, aby zachować historię.
        </Alert>
      )}

      {editMode ? (
        <Box
          mb={6}
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="xl"
          p={4}
        >
          <HStack
            spacing={3}
            mb={3}
            align="flex-start"
            flexDirection={{ base: "column", lg: "row" }}
          >
            <FormControl isRequired flex="1" minW={0}>
              <FormLabel mb={1} fontSize="sm">
                Kod technologii
              </FormLabel>
              <Input
                size="sm"
                value={form.code}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    code: e.target.value.toLocaleUpperCase("pl-PL"),
                  }))
                }
                placeholder="TECH-001"
                borderRadius="lg"
              />
            </FormControl>
            <FormControl isRequired flex="1" minW={0}>
              <FormLabel mb={1} fontSize="sm">
                Nazwa
              </FormLabel>
              <Input
                size="sm"
                value={form.name}
                onChange={(e) =>
                  setForm((s) => ({ ...s, name: e.target.value }))
                }
                placeholder="Nazwa technologii"
                borderRadius="lg"
              />
            </FormControl>
          </HStack>
          <HStack spacing={3} mb={3} align="flex-end">
            <FormControl maxW={{ base: "auto", md: "180px" }}>
              <FormLabel mb={1} fontSize="sm">
                Wersja
              </FormLabel>
              <Input
                size="sm"
                type="number"
                value={form.version}
                onChange={(e) =>
                  setForm((s) => ({ ...s, version: e.target.value }))
                }
                isDisabled
                borderRadius="lg"
              />
            </FormControl>
            <FormControl h="full">
              <FormLabel mb={1} fontSize="sm">
                Aktywna
              </FormLabel>
              <Checkbox
                pt={2}
                isChecked={form.is_active}
                onChange={(e) =>
                  setForm((s) => ({ ...s, is_active: e.target.checked }))
                }
              >
                Tak
              </Checkbox>
            </FormControl>
          </HStack>
          <FormControl>
            <FormLabel mb={1} fontSize="sm">
              Opis
            </FormLabel>
            <Textarea
              size="sm"
              value={form.description}
              onChange={(e) =>
                setForm((s) => ({ ...s, description: e.target.value }))
              }
              placeholder="Opcjonalnie"
              borderRadius="lg"
              minH="88px"
            />
          </FormControl>
          <Box
            mt={3}
            p={3}
            borderWidth="1px"
            borderColor={panelBorder}
            borderRadius="lg"
            bg={sectionBackground}
          >
            <Flex
              gap={4}
              alignItems="center"
              direction={{ base: "column", md: "row" }}
            >
              <FormControl>
                <FormLabel mb={0} fontSize="sm" fontWeight="bold">
                  Termin przydatności przy produkcji
                </FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Określa, czy technologia wymusza obliczanie daty ważności przy
                  tworzeniu meldunku.
                </Text>
                <Checkbox
                  isChecked={form.requires_expiration_date}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      requires_expiration_date: e.target.checked,
                    }))
                  }
                  colorScheme="orange"
                >
                  Uwzględnij termin przydatności
                </Checkbox>
                {mainProductRequiresExpiration &&
                  !form.requires_expiration_date && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Uwaga: Główny produkt wyjściowy wymaga daty ważności. Ta
                      opcja musi być zaznaczona!
                    </Text>
                  )}
              </FormControl>

              {form.requires_expiration_date && (
                <FormControl w={{ base: "full", md: "250px" }}>
                  <FormLabel mb={1} fontSize="sm">
                    Dni przydatności do spożycia
                  </FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    value={form.validity_days}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, validity_days: e.target.value }))
                    }
                    placeholder="Np. 180"
                    borderRadius="lg"
                  />
                </FormControl>
              )}
            </Flex>
          </Box>
        </Box>
      ) : (
        <Box mb={6}>
          <HStack spacing={6} mb={2}>
            <Text>Wersja: {data.version}</Text>
            <Text>Utworzono: {fmt(data.created_at)}</Text>
          </HStack>
          {data.requires_expiration_date && (
            <HStack mb={2}>
              <Badge colorScheme="orange">Przydatność do spożycia</Badge>
              <Text fontSize="sm" color="gray.600">
                Ważność:{" "}
                {data.validity_days
                  ? `${data.validity_days} dni`
                  : "Nie określono"}
              </Text>
            </HStack>
          )}
          <Text color="gray.600">{data.description || "—"}</Text>
        </Box>
      )}

      <HStack justify="space-between" mb={2} align="center">
        <Text fontWeight="bold" fontSize="sm">
          Wejścia (inputs)
        </Text>
      </HStack>
      {editMode && inputProductConflicts.length > 0 && (
        <Alert status="error" borderRadius="md" mb={3} alignItems="flex-start">
          <AlertIcon mt={1} />
          <Box>
            <Text fontWeight="semibold">
              Materiał został użyty więcej niż raz
            </Text>
            <Text fontSize="sm">
              Każdy materiał może wystąpić tylko raz w wejściach technologii —
              jako materiał podstawowy albo jako zamiennik. Usuń powtórzenie lub
              wybierz inny produkt.
            </Text>
            {inputProductConflicts.map((conflict) => {
              const label =
                conflict.usages.find((usage) => usage.productLabel)
                  ?.productLabel ?? "Nieznany materiał"
              return (
                <Text key={conflict.productId} fontSize="sm" mt={1}>
                  Produkt „{label}”:{" "}
                  {conflict.usages
                    .map(formatTechnologyInputProductUsage)
                    .join(" oraz ")}
                  .
                </Text>
              )
            })}
          </Box>
        </Alert>
      )}
      <TableContainer
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="xl"
        overflowX="auto"
        mb={6}
      >
        <Table size="sm" sx={{ tableLayout: "fixed" }}>
          <Thead>
            <Tr>
              <Th>Produkt</Th>
              <Th>SKU</Th>
              <Th w="120px">Ilość</Th>
              <Th w="120px">Strata %</Th>
              <Th w="160px">Koszt</Th>
              {editMode && <Th w="180px">Akcja</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {editMode
              ? inputs.map((row, idx) => {
                  const prod = selectedProducts[row.product_id]
                  return (
                    <Fragment key={`input-${idx}`}>
                      <Tr _hover={{ bg: rowHover }}>
                        <Td py={2}>
                          <RowProductSelector
                            size="sm"
                            value={prod ? `${prod.sku} — ${prod.name}` : ""}
                            onOpenPicker={() => openPicker("input", idx)}
                            showLabel={false}
                            placeholder="Nie wybrano produktu"
                          />
                        </Td>
                        <Td>{prod ? prod.sku : "—"}</Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            step="any"
                            value={row.qty}
                            onChange={(e) => {
                              const value = e.target.value
                              setInputs((prev) => {
                                const next = [...prev]
                                next[idx] = { ...next[idx], qty: value }
                                return next
                              })
                            }}
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            step="any"
                            value={row.loss_percent}
                            onChange={(e) => {
                              const value = e.target.value
                              setInputs((prev) => {
                                const next = [...prev]
                                next[idx] = {
                                  ...next[idx],
                                  loss_percent: value,
                                }
                                return next
                              })
                            }}
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            step="0.0001"
                            value={row.service_cost_netto}
                            isDisabled={!prod?.is_service}
                            placeholder={
                              prod?.is_service ? "Koszt usługi" : "Historia PZ"
                            }
                            onChange={(e) => {
                              const value = e.target.value
                              setInputs((prev) => {
                                const next = [...prev]
                                next[idx] = {
                                  ...next[idx],
                                  service_cost_netto: value,
                                }
                                return next
                              })
                            }}
                          />
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              colorScheme="purple"
                              variant="ghost"
                              onClick={() =>
                                setInputs((prev) => {
                                  const next = [...prev]
                                  next[idx] = {
                                    ...next[idx],
                                    substitutes: [
                                      ...next[idx].substitutes,
                                      {
                                        product_id: "",
                                        qty: "",
                                        loss_percent: "",
                                        service_cost_netto: "",
                                        priority: `${next[idx].substitutes.length + 1}`,
                                        note: "",
                                      },
                                    ],
                                  }
                                  return next
                                })
                              }
                            >
                              + Zamiennik
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() =>
                                setInputs((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                )
                              }
                              isDisabled={inputs.length === 1}
                            >
                              Usuń
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                      {row.substitutes.length > 0 && (
                        <Tr bg="purple.50">
                          <Td colSpan={6} pl={8} py={2}>
                            <VStack align="stretch" spacing={2}>
                              <Text
                                fontSize="xs"
                                fontWeight="bold"
                                color="purple.700"
                              >
                                Zamienniki materiału:
                              </Text>
                              <Grid
                                templateColumns="minmax(280px, 1fr) 90px 130px 70px minmax(160px, 220px) 90px 52px"
                                gap={2}
                                minW="1050px"
                                px={1}
                              >
                                {[
                                  "Produkt zamienny",
                                  "Ilość",
                                  "Koszt",
                                  "Priorytet",
                                  "Uwagi",
                                  "Strata %",
                                  "",
                                ].map((label, labelIndex) => (
                                  <Text
                                    key={`${label}-${labelIndex}`}
                                    fontSize="10px"
                                    fontWeight="semibold"
                                    color="purple.700"
                                    textTransform="uppercase"
                                  >
                                    {label}
                                  </Text>
                                ))}
                              </Grid>
                              {row.substitutes.map((sub, subIndex) => {
                                const substituteProduct =
                                  selectedProducts[sub.product_id]
                                return (
                                  <Grid
                                    key={`sub-${idx}-${subIndex}`}
                                    templateColumns="minmax(280px, 1fr) 90px 130px 70px minmax(160px, 220px) 90px 52px"
                                    gap={2}
                                    minW="1050px"
                                    alignItems="center"
                                  >
                                    <RowProductSelector
                                      size="sm"
                                      value={
                                        substituteProduct
                                          ? `${substituteProduct.sku} — ${substituteProduct.name}`
                                          : ""
                                      }
                                      onOpenPicker={() =>
                                        openPicker("substitute", idx, subIndex)
                                      }
                                      showLabel={false}
                                      placeholder="Wybierz zamiennik"
                                    />
                                    <Input
                                      size="xs"
                                      w="90px"
                                      type="number"
                                      step="any"
                                      placeholder="Ilość"
                                      value={sub.qty}
                                      onChange={(e) => {
                                        const value = e.target.value
                                        setInputs((prev) => {
                                          const next = [...prev]
                                          const substitutes = [
                                            ...next[idx].substitutes,
                                          ]
                                          substitutes[subIndex] = {
                                            ...substitutes[subIndex],
                                            qty: value,
                                          }
                                          next[idx] = {
                                            ...next[idx],
                                            substitutes,
                                          }
                                          return next
                                        })
                                      }}
                                    />
                                    <Input
                                      size="xs"
                                      w="130px"
                                      type="number"
                                      step="0.0001"
                                      placeholder={
                                        substituteProduct?.is_service
                                          ? "Koszt netto usługi"
                                          : "Historia PZ"
                                      }
                                      isDisabled={
                                        !substituteProduct?.is_service
                                      }
                                      value={sub.service_cost_netto}
                                      onChange={(e) => {
                                        const value = e.target.value
                                        setInputs((prev) => {
                                          const next = [...prev]
                                          const substitutes = [
                                            ...next[idx].substitutes,
                                          ]
                                          substitutes[subIndex] = {
                                            ...substitutes[subIndex],
                                            service_cost_netto: value,
                                          }
                                          next[idx] = {
                                            ...next[idx],
                                            substitutes,
                                          }
                                          return next
                                        })
                                      }}
                                    />
                                    <Input
                                      size="xs"
                                      w="70px"
                                      type="number"
                                      min={1}
                                      step={1}
                                      placeholder="Priorytet"
                                      value={sub.priority}
                                      onChange={(e) => {
                                        const value = e.target.value
                                        setInputs((prev) => {
                                          const next = [...prev]
                                          const substitutes = [
                                            ...next[idx].substitutes,
                                          ]
                                          substitutes[subIndex] = {
                                            ...substitutes[subIndex],
                                            priority: value,
                                          }
                                          next[idx] = {
                                            ...next[idx],
                                            substitutes,
                                          }
                                          return next
                                        })
                                      }}
                                    />
                                    <Input
                                      size="xs"
                                      flex="1"
                                      placeholder="Uwagi"
                                      value={sub.note}
                                      onChange={(e) => {
                                        const value = e.target.value
                                        setInputs((prev) => {
                                          const next = [...prev]
                                          const substitutes = [
                                            ...next[idx].substitutes,
                                          ]
                                          substitutes[subIndex] = {
                                            ...substitutes[subIndex],
                                            note: value,
                                          }
                                          next[idx] = {
                                            ...next[idx],
                                            substitutes,
                                          }
                                          return next
                                        })
                                      }}
                                    />
                                    <Input
                                      size="xs"
                                      w="90px"
                                      type="number"
                                      step="any"
                                      placeholder="Strata %"
                                      value={sub.loss_percent}
                                      onChange={(e) => {
                                        const value = e.target.value
                                        setInputs((prev) => {
                                          const next = [...prev]
                                          const substitutes = [
                                            ...next[idx].substitutes,
                                          ]
                                          substitutes[subIndex] = {
                                            ...substitutes[subIndex],
                                            loss_percent: value,
                                          }
                                          next[idx] = {
                                            ...next[idx],
                                            substitutes,
                                          }
                                          return next
                                        })
                                      }}
                                    />
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() =>
                                        setInputs((prev) => {
                                          const next = [...prev]
                                          next[idx] = {
                                            ...next[idx],
                                            substitutes: next[
                                              idx
                                            ].substitutes.filter(
                                              (_, i) => i !== subIndex,
                                            ),
                                          }
                                          return next
                                        })
                                      }
                                    >
                                      Usuń
                                    </Button>
                                  </Grid>
                                )
                              })}
                            </VStack>
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  )
                })
              : data.inputs.map((row) => {
                  const prod =
                    selectedProducts[row.product_id] ??
                    ((row as any).product as any) ??
                    undefined
                  return (
                    <Fragment key={row.id}>
                      <Tr _hover={{ bg: rowHover }}>
                        <Td fontWeight="medium">
                          {prod ? prod.name : row.product_id}
                        </Td>
                        <Td>{prod ? prod.sku : "—"}</Td>
                        <Td>{row.qty}</Td>
                        <Td>{row.loss_percent}%</Td>
                        <Td>
                          {prod?.is_service
                            ? fmtMoney(row.service_cost_netto)
                            : "Historia PZ"}
                        </Td>
                      </Tr>
                      {row.substitutes && row.substitutes.length > 0 && (
                        <Tr bg={sectionBackground}>
                          <Td colSpan={5} pl={8} py={2}>
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              color="purple.600"
                              mb={1}
                            >
                              Zamienniki materiału:
                            </Text>
                            <VStack align="stretch" spacing={1}>
                              {row.substitutes.map((sub) => (
                                <HStack key={sub.id} fontSize="xs">
                                  <Badge colorScheme="purple" fontSize="0.75em">
                                    Zamiennik #{sub.priority}
                                  </Badge>
                                  <Text fontWeight="semibold">
                                    {sub.product?.name ??
                                      sub.substitute_product_id}{" "}
                                    ({sub.product?.sku})
                                  </Text>
                                  <Text>
                                    Ilość: <strong>{sub.qty}</strong>
                                  </Text>
                                  {sub.loss_percent > 0 && (
                                    <Text>Strata: {sub.loss_percent}%</Text>
                                  )}
                                  {sub.note && (
                                    <Text color="gray.500">({sub.note})</Text>
                                  )}
                                </HStack>
                              ))}
                            </VStack>
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  )
                })}
          </Tbody>
        </Table>
      </TableContainer>

      {editMode && (
        <HStack mb={6} spacing={3}>
          <Button
            size="sm"
            colorScheme="green"
            onClick={() =>
              setInputs((prev) => [
                ...prev,
                {
                  product_id: "",
                  qty: "",
                  loss_percent: "",
                  cost_mode: "purchase_history",
                  service_cost_netto: "",
                  service_cost_source: "technology_fixed_rate",
                  cost_note: "",
                  substitutes: [],
                },
              ])
            }
          >
            Dodaj wejście
          </Button>
          {!inputsValid && (
            <Text color="red.500">
              Wejścia wymagają produktu i ilości &gt; 0. Dla usług wymagany jest
              koszt netto &gt; 0.
            </Text>
          )}
        </HStack>
      )}

      <HStack justify="space-between" mb={2} align="center">
        <Text fontWeight="bold" fontSize="sm">
          Wyjścia (outputs)
        </Text>
      </HStack>
      <TableContainer
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="xl"
        overflowX="auto"
        mb={6}
      >
        <Table size="sm" sx={{ tableLayout: "fixed" }}>
          <Thead>
            <Tr>
              <Th>Produkt</Th>
              <Th>SKU</Th>
              <Th w="120px">Ilość</Th>
              <Th w="130px">Referencyjny</Th>
              {editMode && <Th w="80px">Akcja</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {editMode
              ? outputs.map((row, idx) => {
                  const prod = selectedProducts[row.product_id]
                  return (
                    <Tr key={`output-${idx}`} _hover={{ bg: rowHover }}>
                      <Td py={2}>
                        <RowProductSelector
                          size="sm"
                          value={prod ? `${prod.sku} — ${prod.name}` : ""}
                          onOpenPicker={() => openPicker("output", idx)}
                          showLabel={false}
                          placeholder="Nie wybrano produktu"
                        />
                      </Td>
                      <Td>{prod ? prod.sku : "—"}</Td>
                      <Td>
                        <Input
                          size="sm"
                          type="number"
                          step="any"
                          value={row.qty}
                          onChange={(e) => {
                            const value = e.target.value
                            setOutputs((prev) => {
                              const next = [...prev]
                              next[idx] = { ...next[idx], qty: value }
                              return next
                            })
                          }}
                        />
                      </Td>
                      <Td>
                        <Radio
                          isChecked={row.is_main}
                          onChange={() => {
                            setOutputs((prev) => {
                              return prev.map((item, itemIdx) => ({
                                ...item,
                                is_main: itemIdx === idx,
                              }))
                            })
                          }}
                        />
                      </Td>
                      <Td>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            setOutputs((prev) => {
                              const next = prev.filter((_, i) => i !== idx)
                              if (
                                next.length > 0 &&
                                !next.some((item) => item.is_main)
                              ) {
                                next[0] = { ...next[0], is_main: true }
                              }
                              return next
                            })
                          }}
                          isDisabled={outputs.length === 1}
                        >
                          Usuń
                        </Button>
                      </Td>
                    </Tr>
                  )
                })
              : data.outputs.map((row) => {
                  const prod =
                    selectedProducts[row.product_id] ??
                    ((row as any).product as any) ??
                    undefined
                  return (
                    <Tr key={row.id} _hover={{ bg: rowHover }}>
                      <Td>
                        <HStack spacing={2}>
                          <Text>{prod ? prod.name : row.product_id}</Text>
                          {row.is_main && (
                            <Badge colorScheme="orange">Referencyjny</Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td>{prod ? prod.sku : "—"}</Td>
                      <Td>{row.qty}</Td>
                      <Td>{row.is_main ? "Tak" : "Nie"}</Td>
                    </Tr>
                  )
                })}
          </Tbody>
        </Table>
      </TableContainer>
      {editMode && (
        <HStack mb={2} spacing={3}>
          <Button
            size="sm"
            colorScheme="green"
            onClick={() =>
              setOutputs((prev) => [
                ...prev,
                { product_id: "", qty: "", is_main: false },
              ])
            }
          >
            Dodaj wyjście
          </Button>
          {!outputsValid && (
            <Text color="red.500">
              Wyjścia wymagają produktu i ilości &gt; 0. Jedno wyjście musi być
              oznaczone jako referencyjne.
            </Text>
          )}
          <Text color="gray.500" fontSize="sm">
            Zaznacz dokładnie jeden produkt referencyjny. Jego ilość steruje
            skalowaniem zlecenia; pozostałe wyjścia też zostaną wyprodukowane
            proporcjonalnie.
          </Text>
        </HStack>
      )}

      <UnifiedProductPickerModal
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false)
          setPickerTarget(null)
        }}
        title="Wybierz produkt"
        catalogOnly
        highlightedProductIds={highlightedProductIds}
        onChoose={(_, __, ___, ____, product) => {
          if (product) handleChooseProduct(product)
        }}
      />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {isUsedInProductionOrders
                ? "Archiwizować technologię?"
                : "Usunąć technologię?"}
            </AlertDialogHeader>
            <AlertDialogBody>
              {isUsedInProductionOrders
                ? "Technologia jest używana w zleceniach produkcyjnych, więc zostanie zarchiwizowana i ukryta z list aktywnych."
                : "Ta operacja jest nieodwracalna."}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="outline">
                Anuluj
              </Button>
              <Button
                colorScheme={isUsedInProductionOrders ? "orange" : "red"}
                onClick={() => {
                  onClose()
                  deleteTech()
                }}
                ml={3}
                isLoading={isDeleting}
              >
                {isUsedInProductionOrders ? "Archiwizuj" : "Usuń"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
